import React from "react";
import { useNavigate } from "react-router";

import { makeStyles } from "tss-react/mui";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Chip from "./Chip";

const useStyles = makeStyles()((theme) => ({
  TileBox: {
    borderRadius: "10px",
    alignItems: "center",
    height: 100,
    display: "grid",
    width: "100%",
    textAlign: "center",
    cursor: "pointer",
    position: "relative",
  },
  TileProgress: {
    position: "absolute",
    right: 5,
    bottom: 5,
    display: "flex",
  },
  BoxGrid: {
    display: "grid",
    alignItems: "center",
    justifyItems: "center",
    height: "100%",
    "& IMG": {
      width: "30px",
    },
  },
  BoxTypography: {
    display: "grid",
    justifyItems: "left",
    height: "100%",
    paddingLeft: "30px",
    "& .Button": {
      alignItems: "flex-end",
      display: "flex",
    },
    "& .h5": {
      alignItems: "start",
      display: "flex",
    },
  },
  blockquoteContent: {
    width: "100%",
    display: "grid",
    overflow: "hidden",
    borderRadius: "10px",
    padding: "15px 20px 20px 20px",
    height: "100%",
  },
  captionBox: {
    margin: "10px 0",
  },
  BigTileTypography: {
    display: "grid",
    justifyItems: "left",
    position: "relative",
    alignContent: "start",
    wordBreak: "break-word",
    "& P": {
      lineHeight: "1.50",
      marginBottom: 0,
    },
    "& .MuiBox-root .MuiTypography-caption": {
      lineHeight: "1.66",
      display: "block",
    },
    "& .TopIcon IMG, .BottomIcon IMG": {
      height: "20px",
    },
    "& .BottomIcon": {
      opacity: "0.15",
      marginLeft: "10px",
    },
  },
  IconContainer: {
    justifyItems: "right",
  },
  Formtile: {
    "& .TopIcon": {
      height: "20px",
      justifySelf: "end",
    },
  },
}));

const SmallTile = React.memo(({ dataId, link, label, value, bgcolor, color, icon, loading, process }) => {
  const navigate = useNavigate();
  const { classes } = useStyles();

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 3 }}>
      <Box data-id={!dataId ? null : dataId + "Tile"}>
        <Paper elevation={1} className={classes.TileBox} onClick={() => navigate(link)}>
          <Grid container direction="row" alignItems={"stretch"} height={"inherit"}>
            <Grid
              data-id={!dataId ? null : dataId + "TileIcon"}
              size={!label && !value ? 12 : 4}
              bgcolor={bgcolor}
              color={color}
              sx={!label && !value ? { borderRadius: "10px" } : { borderRadius: "10px 0 0 10px" }}
            >
              <Box className={classes.BoxGrid}>{icon}</Box>
            </Grid>
            {label || value ? (
              <Grid data-id={!dataId ? null : dataId + "TileLabel"} size={8} className={classes.BoxTypography}>
                <Box className={"Button"}>
                  <Typography variant="button" textTransform="capitalize">
                    {loading ? <Skeleton variant="rectangular" width={100} sx={{ mb: 1, borderRadius: 1 }} /> : label}
                  </Typography>
                </Box>
                <Box className={"h5"}>
                  <Typography variant="h5" data-id={!dataId ? null : dataId + "TileValue"}>
                    {loading ? (
                      <Skeleton variant="rectangular" width={20} height={30} sx={{ borderRadius: 1 }} />
                    ) : (
                      value
                    )}
                  </Typography>
                </Box>
              </Grid>
            ) : (
              ""
            )}
          </Grid>
          <div className={classes.TileProgress} data-id={!dataId ? null : dataId + "TileProgress"}>
            {process || ""}
          </div>
        </Paper>
      </Box>
    </Grid>
  );
});

const TextComponent = ({ label, value, paragraph = false, style = {} }) => {
  return (
    <Typography variant="caption" textTransform="none" paragraph={paragraph} style={style}>
      {label ? <strong>{label}: </strong> : ""}
      {value}
    </Typography>
  );
};

/**
 * 
 * configData options
  dataId, borderLeft, bgcolor, cursor, clickEvent, editIcon, iconWidth, label, deleteIcon, description, status, chipTextColor, chipBGColor, location, totalFields, createdBy, icon, chips, fileType, textTransform, buttonDisplay, button, process
*/
const BigTile = ({ configData, style }) => {
  const { classes } = useStyles();
  return (
    <blockquote style={{ margin: 0, display: "contents" }}>
      <Paper
        data-id={!configData.dataId ? null : configData.dataId + "Tile"}
        elevation={1}
        className={classes.blockquoteContent}
        sx={{
          borderLeft: configData.borderLeft || "",
          bgcolor: configData.bgcolor || "",
          cursor: configData?.isProcessing ? "no-drop" : configData.cursor || "default",
          position: "relative",
        }}
        style={{ ...style }}
        onClick={
          !configData?.isProcessing && configData.clickEvent ? (e) => configData.clickEvent(configData.link) : undefined
        }
      >
        <Grid container direction="row" height={"100%"}>
          <Grid container direction="row" height={"fit-content"}>
            <Grid
              data-id={!configData.dataId ? null : configData.dataId + "TileLabel"}
              size={
                configData.editIcon || configData.deleteIcon
                  ? configData.iconWidth
                    ? 12 - configData.iconWidth
                    : 10
                  : 12
              }
              className={classes.BigTileTypography}
            >
              <Typography variant="h6" textTransform="none">
                {configData.label}
              </Typography>
            </Grid>
            {configData.deleteIcon ? (
              <Grid
                data-id={!configData.dataId ? null : configData.dataId + "TileDelete"}
                size={configData.iconWidth || 1}
                className={`${classes.BigTileTypography} ${classes.IconContainer}`}
              >
                <Box className={"TopIcon"}>{configData.deleteIcon}</Box>
              </Grid>
            ) : (
              ""
            )}
            {configData.editIcon ? (
              <Grid
                data-id={!configData.dataId ? null : configData.dataId + "TileEdit"}
                size={configData.iconWidth || 1}
                className={`${classes.BigTileTypography} ${classes.IconContainer}`}
              >
                <Box className={"TopIcon"}>{configData.editIcon}</Box>
              </Grid>
            ) : (
              ""
            )}
          </Grid>

          <Grid
            data-id={!configData.dataId ? null : configData.dataId + "TileDescr"}
            size={12}
            className={classes.BigTileTypography}
            alignItems={"end"}
          >
            {configData.description ? (
              <Box component="div" overflow="hidden" textOverflow="ellipsis" maxHeight={37}>
                {configData.description ? configData.description : ""}
              </Box>
            ) : (
              ""
            )}
            <Box className={classes.captionBox}>
              {configData.status ? (
                <Chip
                  label={configData.status}
                  style={{
                    color: configData.chipTextColor,
                    backgroundColor: configData.chipBGColor,
                  }}
                  size="small"
                  variant="filled"
                />
              ) : (
                ""
              )}
              {configData.location ? configData.location : ""}
              {configData.totalFields ? configData.totalFields : ""}
              {configData.createdBy ? configData.createdBy : ""}
            </Box>
          </Grid>
          {configData.chips?.map(({ text, chipTextColor, chipBGColor }) => (
            <Chip
              key={text}
              label={text}
              sx={{ textTransform: configData.textTransform, color: chipTextColor, backgroundColor: chipBGColor }}
              size="small"
              variant="filled"
            />
          ))}

          {configData.icon && (
            <Box
              item
              data-id={!configData.dataId ? null : configData.dataId + "TileIcon"}
              xs={1}
              className={classes.BigTileTypography}
              style={{ marginLeft: "auto" }}
            >
              <Box className={"BottomIcon"}>{configData.icon}</Box>
            </Box>
          )}

          {configData.buttonDisplay ? (
            <Grid container direction="row" alignItems={"end"}>
              <Grid
                data-id={!configData.dataId ? null : configData.dataId + "TileBtn"}
                size={12}
                className={classes.BigTileTypography}
                sx={{ justifyItems: "center !important" }}
              >
                {configData.button ? configData.button : ""}
              </Grid>
            </Grid>
          ) : (
            ""
          )}
        </Grid>
        {configData?.isProcessing ? (
          <div
            className={classes.TileProgress}
            data-id={!configData.dataId ? null : configData.dataId + "TileProgress"}
          >
            {configData?.process ? configData.process : ""}
          </div>
        ) : (
          ""
        )}
      </Paper>
    </blockquote>
  );
};

const FormTile = ({ children, style }) => {
  const { classes } = useStyles();

  return (
    <blockquote className={classes.Formtile} style={{ margin: 0, display: "contents" }}>
      <Paper elevation={1} className={classes.blockquoteContent} style={{ ...style }}>
        <Grid container direction="row" height={"fit-content"}>
          <Grid size={12} className={classes.BigTileTypography} alignItems={"end"}>
            <Box component="div" sx={{ width: "100%" }}>
              {children}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </blockquote>
  );
};

export { SmallTile, BigTile, FormTile, TextComponent };
